import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Vehicle } from "@/interfaces";
import { errorHandler, formatToBrl } from "@/lib/utils";
import {
  lastTrackerPositionService,
  type LocationData,
} from "@/services/api/lastTrackerPosition";
import { format } from "date-fns";
import {
  Loader2,
  MapPin,
  PlusCircle,
  Route,
  ScanEye,
  User,
} from "lucide-react";
import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Marker, Popup } from "react-leaflet";
import { VehicleIcon } from "../Icons";
import Sidebar from "../Sidebar";

const getHeading = (direction: number) => {
  if (direction >= 0 && direction <= 7) {
    return direction * 45;
  }
  return 360;
};

interface VehicleMarkerProps {
  vehicle: Vehicle;
  index: number;
  onAddressInfo: (info: LocationData) => void;
  onAlert?: boolean;
  onVehicleClick?: (vehicleId: number) => void;
}

const VehicleMarker = ({
  vehicle,
  index,
  onAddressInfo,
  onAlert = false,
  onVehicleClick,
}: VehicleMarkerProps) => {
  const [addressInfo, setAddressInfo] = useState<LocationData>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const popupRef = useRef(null);

  const fetchAddressInfo = async (lat: number, lng: number) => {
    setIsLoading(true);
    try {
      const response = await lastTrackerPositionService.getAddressInfo(
        lat,
        lng,
      );
      if (!response.errors) {
        const data = response.data;
        setAddressInfo(data);
        setIsSidebarVisible(true);
        if (onAddressInfo) {
          onAddressInfo(data);
        }
      }
    } catch (error) {
      errorHandler(error, "Erro ao buscar informações do endereço");
    } finally {
      setIsLoading(false);
    }
  };

  const location = useLocation();
  const showHeading = location.pathname.startsWith("/my-fleet/overview");

  const lastPosition = vehicle.last_position;

  if (!lastPosition) {
    return null;
  }

  const heading = showHeading ? getHeading(lastPosition.direction) : 0;

  const driverName = lastPosition.driver?.name || "Motorista não identificado";

  return (
    <Marker
      position={[lastPosition.latitude, lastPosition.longitude]}
      key={`vehicle-${index}`}
      icon={VehicleIcon(vehicle, onAlert, heading, showHeading)}
      eventHandlers={
        onVehicleClick ? { click: () => onVehicleClick(vehicle.id) } : undefined
      }
    >
      {isSidebarVisible && (
        <Sidebar
          addressInfo={addressInfo}
          vehicle={vehicle}
          lastPosition={lastPosition}
          onClose={() => setIsSidebarVisible(false)}
          loading={isLoading}
        />
      )}

      <Popup
        minWidth={320}
        maxWidth={320}
        closeOnClick={false}
        autoClose={false}
        ref={popupRef}
        className="vehicle-marker-popup"
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg w-[300px]">
          <div className="p-5 pb-3 bg-linear-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-700 h-50">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                {vehicle.plate}
              </h3>
              <div className="flex items-center">
                <Badge
                  className={`${
                    lastPosition.ignition ? "bg-emerald-500" : "bg-red-500"
                  } text-white rounded-full px-3 py-0.5 text-xs flex items-center shadow-xs`}
                >
                  <div className="w-2 h-2 rounded-full bg-white mr-1.5 animate-pulse"></div>
                  {lastPosition.ignition ? "Ligado" : "Desligado"}
                </Badge>
                <Badge
                  variant="outline"
                  className="ml-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-0 dark:border-gray-600 rounded-full px-3 py-0.5 text-xs shadow-xs"
                >
                  {lastPosition.speed} km/h
                </Badge>
              </div>
            </div>

            {/* Vehicle title and driver */}
            <h2 className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
              {vehicle.title}
            </h2>
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
              <User className="w-3 h-3 mr-1" />
              {driverName}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 mb-1">
              <Button
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-xs py-1 h-9 shadow-xs transition-all duration-200 ease-in-out"
                onClick={() =>
                  window.open(`/my-fleet/overview/${vehicle.id}`, "_blank")
                }
              >
                <Route className="w-4 h-4 mr-1" />
                Ver trajeto
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs h-9 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 shadow-xs transition-all duration-200 ease-in-out"
                onClick={() => {
                  const streetViewUrl = `https://www.google.com/maps?q=&layer=c&cbll=${lastPosition.latitude},${lastPosition.longitude}`;
                  window.open(streetViewUrl, "_blank");
                }}
              >
                <ScanEye className="w-4 h-4 mr-1" /> Street View
              </Button>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 shadow-xs mb-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center">
                <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                Data:
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                {format(new Date(lastPosition.date), "dd/MM/yyyy HH:mm:ss")}
              </span>
            </div>

            {lastPosition.odometer != null && (
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                  Odômetro:
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {formatToBrl(lastPosition.odometer, 0) ?? 0}
                </span>
              </div>
            )}
            {vehicle.has_engine_on_info && (
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <div className="w-2 h-2 rounded-full bg-amber-500 mr-2"></div>
                  Horímetro:
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {formatToBrl(lastPosition.hour_meter, 0) ?? 0} h
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              {vehicle.inside_safe_zone && (
                <div className="flex items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500 mr-1.5 animate-pulse"></div>
                  <span className="text-green-700 dark:text-green-400">
                    Zona segura
                  </span>
                </div>
              )}
              {lastPosition.is_trunk_open !== null && (
                <div className="flex items-center">
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${lastPosition.is_trunk_open === 1 ? "bg-yellow-500 animate-pulse" : "bg-green-500"} mr-1.5`}
                  ></div>
                  <span
                    className={
                      lastPosition.is_trunk_open === 1
                        ? "text-yellow-600 dark:text-yellow-400"
                        : "text-green-600 dark:text-green-400"
                    }
                  >
                    Baú{" "}
                    {lastPosition.is_trunk_open === 1 ? "aberto" : "fechado"}
                  </span>
                </div>
              )}
              {lastPosition.is_door_open !== null && (
                <div className="flex items-center">
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${lastPosition.is_door_open === 1 ? "bg-yellow-500 animate-pulse" : "bg-green-500"} mr-1.5`}
                  ></div>
                  <span
                    className={
                      lastPosition.is_door_open === 1
                        ? "text-yellow-600 dark:text-yellow-400"
                        : "text-green-600 dark:text-green-400"
                    }
                  >
                    Porta{" "}
                    {lastPosition.is_door_open === 1 ? "aberta" : "fechada"}
                  </span>
                </div>
              )}
              {lastPosition.is_lock_open !== null && (
                <div className="flex items-center">
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${lastPosition.is_lock_open === 1 ? "bg-yellow-500 animate-pulse" : "bg-green-500"} mr-1.5`}
                  ></div>
                  <span
                    className={
                      lastPosition.is_lock_open === 1
                        ? "text-yellow-600 dark:text-yellow-400"
                        : "text-green-600 dark:text-green-400"
                    }
                  >
                    Trava{" "}
                    {lastPosition.is_lock_open === 1
                      ? "desacionada"
                      : "acionada"}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full border-blue-400 text-blue-500 hover:bg-blue-500 hover:text-white shadow-xs dark:bg-gray-700 dark:hover:bg-blue-600 dark:border-blue-600 dark:text-blue-300 dark:hoover:bgrange-500 dark:hover:text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  fetchAddressInfo(
                    lastPosition.latitude,
                    lastPosition.longitude,
                  );
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Carregando endereço...
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4 mr-1" />
                    Ver endereço completo
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="w-full border-orange-400 text-orange-500 hover:bg-orange-500 hover:text-white shadow-xs dark:bg-gray-700 dark:hover:bg-orange-600 dark:border-orange-600 dark:text-orange-300 dark:hoover:bgrange-500 dark:hover:text-white"
                onClick={() => {
                  window.open(
                    `/reference-points/create?lat=${lastPosition.latitude}&lng=${lastPosition.longitude}`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                <PlusCircle className="w-4 h-4 mr-1" />
                Adicionar ponto de referência
              </Button>
            </div>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

export default VehicleMarker;
